import React from 'react';
import _ from 'lodash';

import {
  InventoryActivity_TemplateTabDocument,
  InventoryActivity_TemplateTabQuery,
  SaveListingManagementStrategyTemplateGroupMutation,
  SaveListingManagementStrategyTemplateGroup_TemplateInput,
  useInventoryActivity_TemplateTab_TemplateGroupDrawerQuery,
  useSaveListingManagementStrategyTemplateGroupMutation
} from 'generated/graphql';

import Big from 'big.js';
import * as types from './types';
import { gqlCacheUtil } from 'modules';



export interface ITemplate {
  id: string;
  itemId: string;
  itemName: string;
  itemSku: string;
  priceDollars: Big;
}

export interface IData {
  templates: ITemplate[];
  formState: types.IFormState;
}

export function useQuery(templateId: string) {
  const tuple = useInventoryActivity_TemplateTab_TemplateGroupDrawerQuery({
    variables: {
      templateId
    }
  });

  return React.useMemo(
    () => {
      const templateGroup = tuple.data?.listingManagementStrategyTemplateGroup;
      const data: IData = {
        templates: _.map(templateGroup?.templates.nodes ?? [], (node): ITemplate => ({
          id: node.id,
          itemId: node.item.id,
          itemName: node.item.name,
          itemSku: node.item.sku,
          priceDollars: new Big(node.priceDollars)
        })),
        formState: {
          name: templateGroup?.name ?? ''
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSaveListingManagementStrategyTemplateGroupName(templateId: string) {
  const [fnMutate, tuple] = useSaveListingManagementStrategyTemplateGroupMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      await fnMutate({
        variables: {
          input: {
            id: templateId,
            name: formState.name
          }
        },
        updateQueries: __updateQuery
      });
    },
    [
      fnMutate,
      templateId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useSaveListingManagementStrategyTemplateGroupTemplates(templateId: string) {
  const [fnMutate, tuple] = useSaveListingManagementStrategyTemplateGroupMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (items: Array<{ itemId: string; defaultPriceDollars: Big; }>) => {
      await fnMutate({
        variables: {
          input: {
            id: templateId,
            templates: _.map(items, (item): SaveListingManagementStrategyTemplateGroup_TemplateInput => ({
              itemId: item.itemId,
              priceDollars: item.defaultPriceDollars.toFixed(2)
            }))
          }
        },
        updateQueries: __updateQuery
      });
    },
    [
      fnMutate,
      templateId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}




const __updateQuery = gqlCacheUtil.genUpdateQueryMap<
  InventoryActivity_TemplateTabQuery,
  SaveListingManagementStrategyTemplateGroupMutation
>(
  InventoryActivity_TemplateTabDocument,
  (previousState: InventoryActivity_TemplateTabQuery, { mutationResult }) => ({
    ...previousState,
    listingManagementStrategyTemplateGroups: {
      ...previousState.listingManagementStrategyTemplateGroups,
      nodes: _.map(previousState.listingManagementStrategyTemplateGroups.nodes, node => {
        if (node.id === mutationResult.data?.saveListingManagementStrategyTemplateGroup?.id) {
          return {
            ...node,
            name: mutationResult.data.saveListingManagementStrategyTemplateGroup.name,
            templateCount: mutationResult.data.saveListingManagementStrategyTemplateGroup.templateCount
          };
        }

        return node;
      })
    }
  })
);
